import { useAuth } from "@clerk/clerk-react";
import { AlertContext } from "contexts/Alert";
import { BundledAddOnItem, CheckoutContext } from "contexts/Checkout";
import { OrganizationUserContext } from "contexts/Organization";
import { SplashPage, SubscriptionContext } from "contexts/Subscription";
import { useContext } from "react";
import {
  AddOnItemName,
  BannerCouponName,
  CustomDomainInfo,
  SubscriptionType,
} from "schemas/dashboard";
import { Map } from "schemas/functions";
import { Plans } from "schemas/payments";

import { fetcherAuth } from "utils/api";
import { makeDeepCopy } from "utils/updateLocalState";

import { Coupon, StripeCustomer } from "./schema";

type GeneratePreviewInvoiceArgs = {
  plan?: AddOnItemName | Plans | null;
  organizationId?: number;
  customer?: StripeCustomer;
  coupon?: Coupon;
  addOnItems?: BundledAddOnItem[];
  domainInfo?: CustomDomainInfo | null;
  splashPageSource?: SplashPage | null;
  removePromoCode?: boolean; //  If user want to remove default promo code
};

export const useCheckout = () => {
  const { getToken } = useAuth();
  const { setErrorAlert } = useContext(AlertContext);
  const {
    domainInfo,
    invoice,
    customer,
    promoCode,
    setPromoCode,
    setNoNeedsToPay,
    addOnItems,
    setInvoice,
    plan,
    source,
    setAddOnItems,
    setApplyAddOnLoading,
    setInvoiceLoading,
    splashPageSource,
    setPageSource,
    setDomainInfo,
    setSplashPageSource,
    setPlan,
  } = useContext(CheckoutContext);
  const { addOns, subscription } = useContext(SubscriptionContext);
  const isAddOnItem =
    (addOns && plan && plan in addOns) ||
    splashPageSource === SplashPage.va_bundled;

  const { currentOrg } = useContext(OrganizationUserContext);
  const updatePreviewInvoice = async (args: GeneratePreviewInvoiceArgs) => {
    const existingArgs = {
      plan,
      organizationId: currentOrg?.id,
      customer,
      coupon: promoCode,
      addOnItems,
      domainInfo,
      splashPageSource,
      removePromoCode: false,
    };
    if (args.plan) existingArgs.plan = args.plan;
    if (args.organizationId) existingArgs.organizationId = args.organizationId;
    if (args.customer) existingArgs.customer = args.customer;
    if (args.coupon) existingArgs.coupon = args.coupon;
    if (args.addOnItems) existingArgs.addOnItems = args.addOnItems;
    if (args.domainInfo) existingArgs.domainInfo = args.domainInfo;
    if (args.splashPageSource)
      existingArgs.splashPageSource = args.splashPageSource;
    if (args.removePromoCode)
      existingArgs.removePromoCode = args.removePromoCode;
    if (existingArgs.plan) setPlan(existingArgs.plan);
    setPageSource(existingArgs.splashPageSource);
    setDomainInfo(existingArgs.domainInfo);
    setSplashPageSource(existingArgs.splashPageSource);
    setAddOnItems(existingArgs.addOnItems);

    await generatePreviewInvoice(existingArgs);
  };

  const generatePreviewInvoice = async (args: GeneratePreviewInvoiceArgs) => {
    const {
      plan,
      organizationId,
      customer,
      coupon,
      addOnItems,
      domainInfo,
      splashPageSource,
      removePromoCode,
    } = args;

    if (
      (!plan && splashPageSource !== SplashPage.va_bundled) ||
      !addOns ||
      Object.keys(addOns)?.length === 0 ||
      !customer?.id
    )
      return;

    const canOnlyPurchaseOneSubscription =
      !addOns[plan as AddOnItemName]?.can_purchase_multiple &&
      addOns[plan as AddOnItemName]?.recurring;

    const isAddOnItem =
      (addOns && plan && plan in addOns) ||
      splashPageSource === SplashPage.va_bundled;

    if (isAddOnItem && !canOnlyPurchaseOneSubscription) {
      await generateOneTimeInvoice(coupon, addOnItems, removePromoCode);
      return;
    }

    const data: Map = {
      plan,
      source,
      stripeCustomerId: customer?.id,
      country: customer?.address?.country,
      postalCode: customer?.address?.postal_code,
      bundledAddOns: addOnItems,
      domainInfo,
      splashPageSource,
      removePromoCode,
    };
    if (coupon?.id) {
      data.promoCode = coupon.id;
    }
    setInvoiceLoading(true);
    try {
      const { invoice } = await fetcherAuth(
        getToken,
        `/api/organization/${organizationId}/payments/subscription/invoice`,
        "POST",
        {},
        data,
      );
      if (invoice?.amount_due === 0) {
        setNoNeedsToPay(true);
      }
      const promoId = invoice?.discounts?.[0]?.promotion_code;
      const promoName = invoice?.discounts?.[0]?.coupon?.name;
      if (promoId && promoName && promoCode?.id !== promoId) {
        setPromoCode({
          id: promoId,
          name: promoName,
        });
      }
      setInvoice(invoice);
    } catch (error) {
      if (error.message.includes("promotion code")) {
        setPromoCode({
          id: "",
          name: "",
        });
        updatePreviewInvoice({
          ...args,
          coupon: {
            id: "",
            name: "",
          },
        });
      }
      setErrorAlert(error);
    } finally {
      setInvoiceLoading(false);
    }
  };

  const generateOneTimeInvoice = async (
    coupon?: Coupon,
    addOnItems?: BundledAddOnItem[],
    removePromoCode?: boolean,
  ) => {
    if (!currentOrg) return;

    const data: Map = {
      source,
      promoCode: coupon?.id,
      couponName: coupon?.name,
      items: addOnItems,
      removePromoCode,
    };
    setInvoiceLoading(true);
    try {
      const { invoice } = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/payments-add-ons/invoice/create`,
        "POST",
        {},
        data,
      );
      setInvoice(invoice);
    } catch (error) {
      setErrorAlert(error);
    } finally {
      setInvoiceLoading(false);
    }
  };

  const applyPromoCode = async (coupon: Coupon) => {
    if (!currentOrg || !plan) return;
    setPromoCode(coupon);
    updatePreviewInvoice({
      coupon,
    });
  };

  const removePromoCode = async () => {
    if (!currentOrg || !plan) return;
    setPromoCode({ id: "", name: "" });
    updatePreviewInvoice({
      coupon: { id: "", name: "" },
      removePromoCode: true,
    });
  };

  const applyAddOnItem = async (item: BundledAddOnItem) => {
    setApplyAddOnLoading(item.name);
    const copy = makeDeepCopy(addOnItems || []);
    const index = copy.findIndex((x: BundledAddOnItem) => x.name === item.name);
    if (item.quantity === 0) {
      if (index !== -1) {
        // If quantity is 0, remove the item from the list
        copy.splice(index, 1);
      }
    } else {
      if (index !== -1) {
        copy[index].quantity = item.quantity;
      } else {
        copy.push(item);
      }
    }
    await updatePreviewInvoice({
      addOnItems: copy,
    });
    setAddOnItems(copy);
    setApplyAddOnLoading(null);
  };

  const getQuantityOfAddOnFromInvoice = (addOnItemName: AddOnItemName) => {
    if (!invoice) return 0;
    return (
      invoice?.lines?.data?.filter(
        (item) => item.metadata?.item_name === addOnItemName,
      )?.[0]?.quantity || 0
    );
  };

  const canPurchaseMoreEmailVolume = (quantityToAdd: number) => {
    let currCount = quantityToAdd;
    let max_quantity = addOns[AddOnItemName.email_volume_monthly]?.max_quantity;
    const activeEmailVolumes = subscription?.emailVolumes?.filter(
      (ev) =>
        ev.status === SubscriptionType.ACTIVE ||
        ev.status === SubscriptionType.PAST_DUE,
    );
    if (activeEmailVolumes && activeEmailVolumes?.length > 0) {
      currCount += activeEmailVolumes.length;
    }
    if (currCount > max_quantity) {
      return false;
    }
    return true;
  };

  const isVaMoneyBack =
    splashPageSource === SplashPage.va_money_back ||
    (subscription?.bannerPromoCode?.couponName ===
      BannerCouponName.money_back_guarantee &&
      plan === Plans.VA_YEARLY);

  return {
    isAddOnItem,
    applyPromoCode,
    removePromoCode,
    applyAddOnItem,
    getQuantityOfAddOnFromInvoice,
    updatePreviewInvoice,
    canPurchaseMoreEmailVolume,
    isVaMoneyBack,
  };
};
